import React, { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import { Box, Button, Tab, Tabs } from "@mui/material";
import {
  FOUR_R_SOIL_TEST_CREATOR,
  FOUR_R_FERTILIZER_APPLICATION_CREATOR,
} from "../../../Assets/contextFactories_V0";
import SoilTest from "./SoilTest";
import FertilizerApplication4R from "./FertilizerApplication4R";
import RatesSection from "./FourRRatesSection";
import CompleteStep from "./FourRCompleteStep";

const sections = [
  { label: "Soil tests", icon: <ScienceOutlinedIcon fontSize="small" /> },
  { label: "Applications", icon: <WaterDropOutlinedIcon fontSize="small" /> },
  { label: "4R practices", icon: <SpaOutlinedIcon fontSize="small" /> },
  { label: "Review", icon: <TaskAltOutlinedIcon fontSize="small" /> },
];

function isBlank(value) {
  return value === "" || value === null || value === undefined;
}

function soilResultIsInvalid(test) {
  const result = Number(test.result);
  return (
    isBlank(test.result) ||
    !Number.isFinite(result) ||
    result < 0 ||
    (test.nutrient === "OM" && result > 100)
  );
}

function validateSoilTests(soilTests) {
  if (soilTests.length === 0) return { isValid: false, errors: [{}] };

  let foundError = false;
  const errors = soilTests.map((test) => {
    const error = {};
    for (const property of ["nutrient", "test", "date", "units", "frequency"]) {
      error[property] = isBlank(test[property]);
      if (error[property]) foundError = true;
    }
    error.result = soilResultIsInvalid(test);
    if (error.result) foundError = true;
    return error;
  });

  return foundError ? { isValid: false, errors } : { isValid: true, errors: [] };
}

function validateFertilizerApplications(fertilizerApplications) {
  if (fertilizerApplications.length === 0) {
    return { isValid: false, errors: [{}] };
  }

  let foundError = false;
  const errors = fertilizerApplications.map((application) => {
    const error = {};
    for (const property of [
      "application_date",
      "application_method",
      "fertilizer_timing",
      "soil_state",
      "fertilizer_mix_id",
      "application_rate",
      "rate_unit",
    ]) {
      error[property] = isBlank(application[property]);
      if (error[property]) foundError = true;
    }
    if (!isBlank(application.application_rate)) {
      error.application_rate =
        !Number.isFinite(Number(application.application_rate)) ||
        Number(application.application_rate) <= 0;
      if (error.application_rate) foundError = true;
    }
    return error;
  });

  return foundError ? { isValid: false, errors } : { isValid: true, errors: [] };
}

function initialAssessmentSection(assessment, completed) {
  if (completed) return 3;
  if (!validateSoilTests(assessment?.soil_tests || []).isValid) return 0;
  if (!validateFertilizerApplications(assessment?.fertilizer_applications || []).isValid) return 1;
  return 2;
}

export default function FourRAssessment({
  cropYear,
  completed,
  fourRAssessment,
  setFourRAssessment,
  setFourRState,
}) {
  const [activeSection, setActiveSection] = useState(() =>
    initialAssessmentSection(fourRAssessment, completed)
  );
  const [soilTestsErrors, setSoilTestsErrors] = useState([]);
  const [fertilizerApplicationsErrors, setFertilizerApplicationsErrors] = useState([]);
  const soilTests = fourRAssessment?.soil_tests || [];
  const fertilizerApplications = fourRAssessment?.fertilizer_applications || [];
  const soilValidation = validateSoilTests(soilTests);
  const applicationValidation = validateFertilizerApplications(fertilizerApplications);

  const defaultSoilTestError = {
    nutrient: false,
    test: false,
    date: false,
    result: false,
    units: false,
    frequency: false,
  };
  const defaultFertApplicationError = {
    application_date: false,
    application_method: false,
    fertilizer_timing: false,
    soil_state: false,
    fertilizer_mix_id: false,
    application_rate: false,
    rate_unit: false,
  };

  function markAssessmentIncomplete() {
    setFourRState((previous) => ({ ...previous, completed4R: false }));
  }

  function handleAddSoilTest() {
    markAssessmentIncomplete();
    setFourRAssessment((previous) => ({
      ...previous,
      soil_tests: [...(previous.soil_tests || []), FOUR_R_SOIL_TEST_CREATOR()],
    }));
  }

  function handleAddFertilizerApplication() {
    markAssessmentIncomplete();
    setFourRAssessment((previous) => ({
      ...previous,
      fertilizer_applications: [
        ...(previous.fertilizer_applications || []),
        FOUR_R_FERTILIZER_APPLICATION_CREATOR(),
      ],
    }));
  }

  function validateSection(section) {
    if (section === 0) {
      const result = validateSoilTests(soilTests);
      setSoilTestsErrors(result.errors);
      return result.isValid;
    }
    if (section === 1) {
      const result = validateFertilizerApplications(fertilizerApplications);
      setFertilizerApplicationsErrors(result.errors);
      return result.isValid;
    }
    return true;
  }

  function handleContinue() {
    if (!validateSection(activeSection)) return;
    setActiveSection((current) => Math.min(current + 1, 3));
  }

  function handleFinish() {
    const nextSoilValidation = validateSoilTests(soilTests);
    const nextApplicationValidation = validateFertilizerApplications(fertilizerApplications);
    setSoilTestsErrors(nextSoilValidation.errors);
    setFertilizerApplicationsErrors(nextApplicationValidation.errors);

    if (!nextSoilValidation.isValid) {
      setActiveSection(0);
      return;
    }
    if (!nextApplicationValidation.isValid) {
      setActiveSection(1);
      return;
    }

    setFourRState((previous) => ({ ...previous, completed4R: true }));
  }

  return (
    <section className="mt-5 border-t border-slate-200 pt-5">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-[19px] font-bold text-slate-900">
              Detailed fertilizer records
            </h4>
            <span className="border border-[#2f6f45] bg-green-50 px-2 py-0.5 text-xs font-bold text-[#2f6f45]">
              4R stewardship
            </span>
          </div>
          <p className="mt-1 max-w-[760px] text-sm leading-6 text-slate-600">
            Connect soil evidence, fertilizer products, application rates, and nutrient-management practices.
          </p>
        </div>
        <div
          className={`flex shrink-0 items-center gap-1.5 text-sm font-bold ${
            completed ? "text-[#2f6f45]" : "text-amber-700"
          }`}
          role="status"
        >
          <CheckCircleOutlineIcon fontSize="small" />
          {completed ? "Assessment complete" : "Needs review"}
        </div>
      </div>

      <Box sx={{ mt: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeSection}
          onChange={(_, value) => setActiveSection(value)}
          aria-label="Detailed fertilizer record sections"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 44 }}
        >
          {sections.map((section, index) => (
            <Tab
              key={section.label}
              icon={section.icon}
              iconPosition="start"
              label={section.label}
              sx={{ minHeight: 44, textTransform: "none", fontWeight: 700 }}
              aria-label={`${section.label}${
                index === 0
                  ? `, ${soilTests.length} record${soilTests.length === 1 ? "" : "s"}`
                  : index === 1
                  ? `, ${fertilizerApplications.length} record${fertilizerApplications.length === 1 ? "" : "s"}`
                  : ""
              }`}
            />
          ))}
        </Tabs>
      </Box>

      <div className="py-4">
        {activeSection === 0 ? (
          <div>
            <div className="mb-3 flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
              <div>
                <h5 className="text-base font-bold text-slate-800">Soil evidence</h5>
                <p className="mt-0.5 text-sm text-slate-600">
                  Enter the lab results used to make nutrient decisions. Organic matter can be included here but is not limited to this assessment elsewhere in the crop year.
                </p>
              </div>
              <Button startIcon={<AddIcon />} onClick={handleAddSoilTest} size="small" variant="outlined">
                Add soil test
              </Button>
            </div>
            <div className="divide-y divide-slate-200 border-y border-slate-200 bg-white">
              {soilTests.map((row, index) => (
                <SoilTest
                  cropYear={cropYear}
                  key={index}
                  index={index}
                  data={row}
                  fourRAssessment={fourRAssessment}
                  setFourRAssessment={setFourRAssessment}
                  onAssessmentChange={markAssessmentIncomplete}
                  error={soilTestsErrors[index] || defaultSoilTestError}
                />
              ))}
            </div>
            {soilTestsErrors.length > 0 ? (
              <p className="mt-3 border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800" role="alert">
                Complete every soil-test field. Results must be zero or greater; percentages cannot exceed 100.
              </p>
            ) : null}
          </div>
        ) : null}

        {activeSection === 1 ? (
          <div>
            <div className="mb-3 flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
              <div>
                <h5 className="text-base font-bold text-slate-800">Product applications</h5>
                <p className="mt-0.5 text-sm text-slate-600">
                  Add one row for each fertilizer product pass, including when, how, and at what rate it was applied.
                </p>
              </div>
              <Button startIcon={<AddIcon />} onClick={handleAddFertilizerApplication} size="small" variant="outlined">
                Add application
              </Button>
            </div>
            <div className="divide-y divide-slate-200 border-y border-slate-200 bg-white">
              {fertilizerApplications.map((row, index) => (
                <FertilizerApplication4R
                  cropYear={cropYear}
                  key={index}
                  index={index}
                  data={row}
                  fourRAssessment={fourRAssessment}
                  setFourRAssessment={setFourRAssessment}
                  onAssessmentChange={markAssessmentIncomplete}
                  error={fertilizerApplicationsErrors[index] || defaultFertApplicationError}
                />
              ))}
            </div>
            {fertilizerApplicationsErrors.length > 0 ? (
              <p className="mt-3 border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800" role="alert">
                Complete the date, method, timing, soil state, product mix, and a rate greater than zero for every application.
              </p>
            ) : null}
          </div>
        ) : null}

        {activeSection === 2 ? (
          <div>
            <h5 className="text-base font-bold text-slate-800">Nutrient-management practices</h5>
            <p className="mb-3 mt-0.5 text-sm text-slate-600">
              Select the decision practices used for nitrogen and phosphorus. These describe stewardship, not additional fertilizer quantities.
            </p>
            <RatesSection
              fourRAssessment={fourRAssessment}
              setFourRAssessment={setFourRAssessment}
              onAssessmentChange={markAssessmentIncomplete}
            />
          </div>
        ) : null}

        {activeSection === 3 ? (
          <CompleteStep
            soilTestCount={soilTests.length}
            applicationCount={fertilizerApplications.length}
            soilTestsValid={soilValidation.isValid}
            applicationsValid={applicationValidation.isValid}
            operationsQuestionnaire={fourRAssessment?.operations_questionnaire}
          />
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-3">
        <Button
          disabled={activeSection === 0}
          onClick={() => setActiveSection((current) => Math.max(0, current - 1))}
        >
          Back
        </Button>
        {activeSection < 3 ? (
          <Button
            aria-label={`Continue 4R assessment from ${
              activeSection === 0
                ? "Soil Testing"
                : activeSection === 1
                ? "Fertilizer Applications"
                : "Fertilizer Rates"
            }`}
            variant="contained"
            onClick={handleContinue}
          >
            Continue
          </Button>
        ) : (
          <Button aria-label="Finish 4R assessment" variant="contained" onClick={handleFinish}>
            {completed ? "Assessment complete" : "Complete assessment"}
          </Button>
        )}
      </div>
    </section>
  );
}
